import type { FlowplayerPlaylistQueue } from "./custom-flowplayer-playlist-ui-load";
import * as classes from "./custom-flowplayer-playlist-ui-styles.module.scss";
import { CAROUSEL_CLASS_NAME } from "../carousel/carousel-init";
import "../carousel/control-prev-next/prev-control-init";
import "../carousel/control-prev-next/next-control-init";
import { CAROUSEL_ITEM_CLASS_NAME } from "../carousel/slider/slider-elements-list";
import { initializeAndRegisterCarouselController } from "../carousel/carousel-handles";
import { CarouselControllerWillChangeSlideEvent } from "../carousel/controller/carousel-controller-will-change-slide-event";
import { CarouselSliderSlideChangeEvent } from "../carousel/slider/carousel-slider-slide-change-event";

export const createCustomFlowplayerPlaylistUi = (
  queue: FlowplayerPlaylistQueue,
  title: string,
  onUserSelectCallback: (
    index: number,
    updateActiveQueueElement: () => void
  ) => void
) => {
  const playlistElement = document.createElement("div");
  playlistElement.classList.add(classes.playList);

  if (title !== "") {
    const titleElement = document.createElement("span");
    titleElement.classList.add(classes.headline);
    titleElement.append(title);

    playlistElement.append(titleElement);
  }

  const playlistCarouselElement = createPlaylistCarousel(
    queue,
    onUserSelectCallback
  );
  playlistElement.append(playlistCarouselElement);

  return {
    node: playlistElement,
    show: () => {
      playlistElement.removeAttribute("hidden");
    },
    hide: () => {
      playlistElement.setAttribute("hidden", "");
    },
    destroy: () => {
      playlistElement.remove();
    },
  } as const;
};

const createPlaylistCarousel = (
  queue: FlowplayerPlaylistQueue,
  onUserSelectCallback: (
    index: number,
    updateActiveQueueElement: () => void
  ) => void
) => {
  const memberListRootElement = document.createElement("ol");
  memberListRootElement.classList.add(classes.memberList);

  for (const [index, queueMember] of queue.members.entries()) {
    const queueMemberElement = document.createElement("li");
    queueMemberElement.classList.add(
      CAROUSEL_ITEM_CLASS_NAME,
      classes.queueItem,
      ...(queue.idx === index ? [classes.queueItemActive] : [])
    );
    queueMemberElement.title = queueMember.title;
    queueMemberElement.addEventListener("click", () =>
      onUserSelectCallback(index, () => {
        const queueElements = memberListRootElement.getElementsByClassName(
          classes.queueItem
        );
        for (const queryElement of queueElements) {
          queryElement.classList.remove(classes.queueItemActive);
        }

        queueMemberElement.classList.add(classes.queueItemActive);
      })
    );

    const thumbnailElement = document.createElement("img");
    thumbnailElement.classList.add(classes.thumbnail);
    thumbnailElement.src = queueMember.poster;
    thumbnailElement.width = 36;
    thumbnailElement.height = 36;

    const titleElement = document.createElement("div");
    titleElement.classList.add(classes.title);
    titleElement.append(queueMember.title);

    queueMemberElement.append(thumbnailElement, titleElement);
    memberListRootElement.append(queueMemberElement);
  }

  return createWrappingCarousel(memberListRootElement);
};

const createWrappingCarousel = (listElement: HTMLOListElement) => {
  listElement.classList.add("carousel-inner");

  const viewWindowElement = document.createElement("div");
  viewWindowElement.classList.add("carousel-view-window", classes.viewThrough);
  viewWindowElement.append(listElement);

  const previousButton = createPrevButton();
  previousButton.classList.add(classes.carouselPrevNextButtonHidden);

  const nextButton = createNextButton();

  const wrappingElement = document.createElement("div");
  wrappingElement.classList.add(CAROUSEL_CLASS_NAME, "carousel__upper-row");

  wrappingElement.append(viewWindowElement, previousButton, nextButton);

  const updateVisibilityOfPrevNextButtons = () => {
    const currentScrollPosition = listElement.scrollLeft;

    const previousButtonWidth = Math.floor(
      previousButton.getBoundingClientRect().width
    );
    if (currentScrollPosition <= previousButtonWidth) {
      previousButton.classList.add(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.remove(classes.viewThroughNotAtStart);
    } else {
      previousButton.classList.remove(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.add(classes.viewThroughNotAtStart);
    }

    const nextButtonWidth = Math.floor(
      nextButton.getBoundingClientRect().width
    );
    const listFurthestViewThroughWindowStart =
      listElement.scrollWidth - viewWindowElement.scrollWidth;
    if (
      currentScrollPosition >=
      listFurthestViewThroughWindowStart - nextButtonWidth
    ) {
      nextButton.classList.add(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.remove(classes.viewThroughNotAtEnd);
    } else {
      nextButton.classList.remove(classes.carouselPrevNextButtonHidden);
      viewWindowElement.classList.add(classes.viewThroughNotAtEnd);
    }
  };

  listElement.addEventListener("scroll", updateVisibilityOfPrevNextButtons, {
    passive: true,
  });

  const carouselController = initializeAndRegisterCarouselController(
    wrappingElement,
    {
      loop: false,
    }
  );
  /**
   * Counter the width of the prev button, if it is visible.
   */
  const setScrollOffsetAccoringToScroll = (
    event:
      | CarouselControllerWillChangeSlideEvent
      | CarouselSliderSlideChangeEvent
  ) => {
    // eslint-disable-next-line no-param-reassign
    event.scrollOffset =
      event.index > 0 ? previousButton.getBoundingClientRect().width : 0;
  };
  carouselController.addEventListener(
    CarouselControllerWillChangeSlideEvent.KEY,
    setScrollOffsetAccoringToScroll
  );
  carouselController.addEventListener(
    CarouselSliderSlideChangeEvent.KEY,
    setScrollOffsetAccoringToScroll
  );
  // We cannot use native scroll-snap here, since it aligns the last child
  // in scroll direction (inline-end when scrolling forward) with inline-end -
  // scroll-padding-inline-end. But we want to align the first child with
  // inline-start + scroll-padding-inline-start.
  //
  // We try to mimic scroll-snap behavior here and don't care about
  // safari not dispatching scrollend event, since this is only an
  // optional enhancement.
  listElement.addEventListener("scrollend", () =>
    carouselController.slideTo(carouselController.getCurrentIndex())
  );

  new ResizeObserver(() => updateVisibilityOfPrevNextButtons()).observe(
    listElement
  );

  return wrappingElement;
};

const createPrevButton = () => {
  const button = createPrevNextButton(
    "Previous",
    "M-4608.225-11253.812l-16.7,16.7,16.7,16.7"
  );
  button.classList.add("carousel-control-prev");
  return button;
};

const createNextButton = () => {
  const button = createPrevNextButton(
    "Next",
    "M-4624.922-11253.812l16.7,16.7-16.7,16.7"
  );
  button.classList.add("carousel-control-next");
  return button;
};

const createPrevNextButton = (label: string, svgPathString: string) => {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.classList.add("icon__chevron");
  path.setAttribute("d", svgPathString);
  path.setAttribute("transform", "translate(4627.043 11254.873)");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.append(path);
  svg.classList.add("icon--pop", classes.carouselPrevNextIcon);
  svg.setAttribute("height", "35.516");
  svg.setAttribute("width", "19.879");
  svg.setAttribute("viewBox", "0 0 19.879 35.516");

  const hiddenLabel = document.createElement("span");
  hiddenLabel.classList.add("sr-only");
  hiddenLabel.append(label);

  const button = document.createElement("button");
  button.append(svg, hiddenLabel);

  return button;
};
